import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  addSalary,
  getEmployees,
  clearError,
  clearSuccess,
} from "../../../../redux/Slices/authSlice";
import { FaArrowLeft } from "react-icons/fa";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const normalizeListResponse = payload => {
  if (!payload) return { items: [], pagination: null };

  let items = [];
  let pagination = null;

  if (Array.isArray(payload?.data?.data)) {
    items = payload.data.data;
    pagination = payload.data?.meta || payload.data?.pagination || null;
  } else if (Array.isArray(payload?.data)) {
    items = payload.data;
    pagination = payload.meta || payload.pagination || null;
  } else if (Array.isArray(payload?.items)) {
    items = payload.items;
    pagination = payload.pagination || payload.meta || null;
  } else if (Array.isArray(payload)) {
    items = payload;
    pagination = null;
  } else {
    items = [];
    pagination = payload.pagination || payload.meta || null;
  }

  // Normalize pagination to ensure it has current_page and last_page
  if (pagination) {
    pagination = {
      current_page: pagination.current_page || pagination.page || 1,
      last_page: pagination.last_page || pagination.total_pages || 1,
      per_page: pagination.per_page || 10,
      total: pagination.total || 0,
    };
  }

  return { items, pagination };
};

const AddSalary = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation("global");
  const isRTL = i18n.language === "ar";
  const { isLoading, error, success } = useSelector(state => state.auth);

  // Employee dropdown state
  const [employeeDropdownOpen, setEmployeeDropdownOpen] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [employeePage, setEmployeePage] = useState(1);
  const [employeePagination, setEmployeePagination] = useState(null);
  const employeeSearchInputRef = useRef(null);
  const [formData, setFormData] = useState({
    employee_id: "",
    base_salary: "",
    allowances: {
      housing: "",
      transportation: "",
    },
    deductions: {
      social_insurance: "",
    },
    effective_date: "",
  });
  const textAlign = isRTL ? "right" : "left";
  const dir = isRTL ? "rtl" : "ltr";
  const lastErrorRef = useRef({ message: null, time: 0 });

  const fetchEmployees = useCallback(
    async (page = employeePage, search = employeeSearchTerm) => {
      try {
        const payload = await dispatch(
          getEmployees({
            page,
            per_page: 10,
            search: search || undefined,
            language: i18n.language,
          }),
        ).unwrap();
        const { items, pagination: paginationMeta } =
          normalizeListResponse(payload);
        setEmployeeOptions(items);
        setEmployeePagination(paginationMeta);
      } catch (error) {
        toast.error(
          error ||
            t("salaries.add.messages.fetchEmployeesError", {
              defaultValue: "فشل جلب الموظفين",
            }),
          { rtl: isRTL },
        );
      }
    },
    [dispatch, t, i18n, isRTL],
  );

  useEffect(() => {
    if (employeeDropdownOpen) {
      fetchEmployees(employeePage, employeeSearchTerm);
    }
  }, [employeeDropdownOpen, employeePage, fetchEmployees]);

  useEffect(() => {
    if (employeeSearchTerm !== undefined && employeeDropdownOpen) {
      const timeoutId = setTimeout(() => {
        setEmployeePage(1);
        fetchEmployees(1, employeeSearchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [employeeSearchTerm, employeeDropdownOpen, fetchEmployees]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      const isClickInsideDropdown = event.target.closest(
        "[data-dropdown-container]",
      );
      const isClickOnDropdownToggle = event.target.closest(
        "[data-dropdown-toggle]",
      );

      if (!isClickInsideDropdown && !isClickOnDropdownToggle) {
        setEmployeeDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (success) {
      toast.success(success, { rtl: isRTL });
      dispatch(clearSuccess());
      navigate("/salaries");
    }
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [success, error, dispatch, navigate, isRTL]);

  const handleInputChange = e => {
    const { name, value } = e.target;

    if (name.startsWith("allowances.")) {
      const field = name.split(".")[1];
      setFormData(prev => ({
        ...prev,
        allowances: {
          ...prev.allowances,
          [field]: value,
        },
      }));
    } else if (name.startsWith("deductions.")) {
      const field = name.split(".")[1];
      setFormData(prev => ({
        ...prev,
        deductions: {
          ...prev.deductions,
          [field]: value,
        },
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Submit the data
  const handleSubmit = e => {
    e.preventDefault();

    if (!formData.employee_id) {
      toast.error(t("salaries.add.validation.selectEmployee"), { rtl: isRTL });
      return;
    }

    if (!formData.base_salary) {
      toast.error(t("salaries.add.validation.enterBaseSalary"), { rtl: isRTL });
      return;
    }

    if (parseFloat(formData.base_salary) < 0) {
      toast.error(t("salaries.add.validation.baseSalaryNegative"), {
        rtl: isRTL,
      });
      return;
    }

    // Prevent SQL numeric overflow (MySQL DECIMAL max ~999,999,999.99)
    const MAX_SALARY = 999999999.99;
    if (parseFloat(formData.base_salary) > MAX_SALARY) {
      toast.error(
        t("salaries.add.validation.baseSalaryTooLarge", {
          defaultValue:
            "قيمة الراتب الأساسي كبيرة جداً، الحد الأقصى المسموح به هو 999,999,999",
        }),
        { rtl: isRTL },
      );
      return;
    }

    if (
      formData.allowances.housing &&
      parseFloat(formData.allowances.housing) < 0
    ) {
      toast.error(t("salaries.add.validation.housingNegative"), { rtl: isRTL });
      return;
    }

    if (
      formData.allowances.transportation &&
      parseFloat(formData.allowances.transportation) < 0
    ) {
      toast.error(t("salaries.add.validation.transportationNegative"), {
        rtl: isRTL,
      });
      return;
    }

    if (
      formData.deductions.social_insurance &&
      parseFloat(formData.deductions.social_insurance) < 0
    ) {
      toast.error(t("salaries.add.validation.socialInsuranceNegative"), {
        rtl: isRTL,
      });
      return;
    }

    if (!formData.effective_date) {
      toast.error(t("salaries.add.validation.enterEffectiveDate"), {
        rtl: isRTL,
      });
      return;
    }

    const salaryData = {
      employee_id: parseInt(formData.employee_id),
      base_salary: parseFloat(formData.base_salary),
      allowances: {
        housing: formData.allowances.housing
          ? parseFloat(formData.allowances.housing)
          : 0,
        transportation: formData.allowances.transportation
          ? parseFloat(formData.allowances.transportation)
          : 0,
      },
      deductions: {
        social_insurance: formData.deductions.social_insurance
          ? parseFloat(formData.deductions.social_insurance)
          : 0,
      },
      effective_date: formData.effective_date,
    };

    dispatch(addSalary(salaryData));
  };

  return (
    <div
      style={{
        padding: "30px",
        backgroundColor: "#14263b",
        minHeight: "calc(100vh - 80px)",
        color: "white",
        direction: dir,
      }}
    >
      {/* العنوان */}
      <div style={{ marginBottom: "30px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "15px",
            marginBottom: "20px",
          }}
        >
          <button
            onClick={() => navigate("/salaries")}
            style={{
              backgroundColor: "#666",
              color: "white",
              border: "none",
              padding: "10px",
              borderRadius: "8px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexDirection: isRTL ? "row" : "row-reverse",
            }}
          >
            <FaArrowLeft />
            {t("salaries.add.back")}
          </button>
          <h1
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              color: "white",
              margin: 0,
              textAlign,
            }}
          >
            {t("salaries.add.title")}
          </h1>
        </div>
      </div>

      <div
        style={{
          backgroundColor: "#1b3350",
          borderRadius: "12px",
          padding: "30px",
          border: "1px solid #333",
        }}
      >
        <form onSubmit={handleSubmit}>
          <div style={{ display: "flex", gap: "30px", flexWrap: "wrap" }}>
            {/* العمود الأيسر */}
            <div style={{ flex: "1", minWidth: "300px" }}>
              {/* الموظف */}
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    color: "white",
                    fontWeight: "500",
                    textAlign,
                  }}
                >
                  {t("salaries.add.fields.employee")}
                </label>
                <div style={{ position: "relative" }} data-dropdown-container>
                  <div
                    data-dropdown-toggle
                    onClick={() => setEmployeeDropdownOpen(prev => !prev)}
                    style={{
                      width: "100%",
                      padding: "12px 15px",
                      backgroundColor: "#1a1f2e",
                      border: "1px solid #333",
                      borderRadius: "8px",
                      color: "white",
                      fontSize: "14px",
                      direction: dir,
                      textAlign,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                  >
                    <span>
                      {formData.employee_id
                        ? (() => {
                            const selected = employeeOptions.find(
                              e =>
                                String(e.id) === String(formData.employee_id),
                            );
                            return selected
                              ? `${(isRTL ? selected.name_ar : selected.name_en) || selected.name} - ${selected.employee_code}`
                              : t("salaries.add.placeholders.selectEmployee");
                          })()
                        : t("salaries.add.placeholders.selectEmployee")}
                    </span>
                    <span style={{ fontSize: 12 }}>▼</span>
                  </div>
                  <DropdownWithSearch
                    isOpen={employeeDropdownOpen}
                    searchTerm={employeeSearchTerm}
                    onSearchChange={setEmployeeSearchTerm}
                    searchInputRef={employeeSearchInputRef}
                    items={employeeOptions}
                    pagination={employeePagination}
                    onPageChange={setEmployeePage}
                    onClose={() => setEmployeeDropdownOpen(false)}
                    onSelect={item => {
                      setFormData(prev => ({
                        ...prev,
                        employee_id: String(item.id),
                      }));
                      setEmployeeDropdownOpen(false);
                      setEmployeeSearchTerm("");
                      setEmployeePage(1);
                    }}
                    label={t("salaries.add.fields.employee")}
                    isRTL={isRTL}
                    i18n={i18n}
                  />
                </div>
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    color: "white",
                    fontWeight: "500",
                    textAlign,
                  }}
                >
                  {t("salaries.add.fields.baseSalary")}
                </label>
                <input
                  type="number"
                  name="base_salary"
                  value={formData.base_salary}
                  onChange={handleInputChange}
                  min="0"
                  max="999999999.99"
                  placeholder={t("salaries.add.placeholders.baseSalary")}
                  style={{
                    width: "100%",
                    padding: "12px 15px",
                    backgroundColor: "#1a1f2e",
                    border: "1px solid #333",
                    borderRadius: "8px",
                    color: "white",
                    fontSize: "14px",
                    textAlign,
                    direction: dir,
                  }}
                />
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    color: "white",
                    fontWeight: "500",
                    textAlign,
                  }}
                >
                  {t("salaries.add.fields.effectiveDate")}
                </label>
                <input
                  type="date"
                  name="effective_date"
                  value={formData.effective_date}
                  onChange={handleInputChange}
                  style={{
                    width: "100%",
                    padding: "12px 15px",
                    backgroundColor: "#1a1f2e",
                    border: "1px solid #333",
                    borderRadius: "8px",
                    color: "white",
                    fontSize: "14px",
                    textAlign,
                    direction: dir,
                  }}
                />
              </div>
            </div>

            <div style={{ flex: "1", minWidth: "300px" }}>
              {/* البدلات */}
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    color: "white",
                    fontWeight: "500",
                    textAlign,
                  }}
                >
                  {t("salaries.add.fields.allowances")}
                </label>

                <div style={{ marginBottom: "15px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      color: "#ccc",
                      fontSize: "12px",
                      textAlign,
                    }}
                  >
                    {t("salaries.add.labels.housing")}
                  </label>
                  <input
                    type="number"
                    name="allowances.housing"
                    value={formData.allowances.housing}
                    onChange={handleInputChange}
                    min="0"
                    placeholder={t("salaries.add.placeholders.housing")}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      backgroundColor: "#1a1f2e",
                      border: "1px solid #333",
                      borderRadius: "6px",
                      color: "white",
                      fontSize: "14px",
                      textAlign,
                      direction: dir,
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      color: "#ccc",
                      fontSize: "12px",
                      textAlign,
                    }}
                  >
                    {t("salaries.add.labels.transportation")}
                  </label>
                  <input
                    type="number"
                    name="allowances.transportation"
                    value={formData.allowances.transportation}
                    onChange={handleInputChange}
                    min="0"
                    placeholder={t("salaries.add.placeholders.transportation")}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      backgroundColor: "#1a1f2e",
                      border: "1px solid #333",
                      borderRadius: "6px",
                      color: "white",
                      fontSize: "14px",
                      textAlign,
                      direction: dir,
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    color: "white",
                    fontWeight: "500",
                    textAlign,
                  }}
                >
                  {t("salaries.add.fields.deductions")}
                </label>

                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      color: "#ccc",
                      fontSize: "12px",
                      textAlign,
                    }}
                  >
                    {t("salaries.add.labels.socialInsurance")}
                  </label>
                  <input
                    type="number"
                    name="deductions.social_insurance"
                    value={formData.deductions.social_insurance}
                    onChange={handleInputChange}
                    min="0"
                    placeholder={t("salaries.add.placeholders.socialInsurance")}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      backgroundColor: "#1a1f2e",
                      border: "1px solid #333",
                      borderRadius: "6px",
                      color: "white",
                      fontSize: "14px",
                      textAlign,
                      direction: dir,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* أزرار الإجراء */}
          <div
            className="action-btns"
            style={{
              display: "flex",
              gap: "15px",
              justifyContent: "center",
              flexDirection: isRTL ? "row" : "row-reverse",
            }}
          >
            <button
              type="button"
              onClick={() => navigate("/salaries")}
              style={{
                backgroundColor: "#666",
                color: "white",
                border: "none",
                padding: "12px 24px",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              {t("salaries.add.buttons.cancel")}
            </button>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                backgroundColor: isLoading ? "#666" : "#AC2000",
                color: "white",
                border: "none",
                padding: "12px 24px",
                borderRadius: "8px",
                cursor: isLoading ? "not-allowed" : "pointer",
                fontSize: "14px",
                fontWeight: "bold",
                opacity: isLoading ? 0.6 : 1,
              }}
            >
              {isLoading
                ? t("salaries.add.buttons.saving")
                : t("salaries.add.buttons.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const dropdownMenuStyle = {
  position: "absolute",
  top: "calc(100% + 4px)",
  left: 0,
  right: 0,
  backgroundColor: "#1a1f2e",
  border: "1px solid #333",
  borderRadius: 8,
  zIndex: 20,
  padding: 12,
  boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
};

const dropdownSearchStyle = {
  width: "100%",
  padding: 10,
  borderRadius: 6,
  border: "1px solid #333",
  backgroundColor: "#14263b",
  color: "white",
  marginBottom: 10,
};

const dropdownEmptyStyle = {
  padding: 12,
  textAlign: "center",
  color: "#ccc",
  fontSize: 13,
};

const DropdownWithSearch = ({
  isOpen,
  searchTerm,
  onSearchChange,
  searchInputRef,
  items,
  pagination,
  onPageChange,
  onSelect,
  onClose,
  label,
  isRTL = true,
  i18n,
}) => {
  const { t } = useTranslation();
  const dropdownIsRTL = isRTL !== undefined ? isRTL : i18n?.language === "ar";

  useEffect(() => {
    if (isOpen && searchInputRef?.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, searchTerm, items, searchInputRef]);

  if (!isOpen) return null;

  return (
    <div
      style={{ ...dropdownMenuStyle, direction: dropdownIsRTL ? "rtl" : "ltr" }}
      onClick={e => e.stopPropagation()}
    >
      <div style={{ padding: 8 }}>
        <input
          ref={searchInputRef}
          type="text"
          placeholder={t("salaries.add.placeholders.searchEmployee")}
          value={searchTerm}
          onChange={e => {
            onSearchChange(e.target.value);
            setTimeout(() => {
              searchInputRef.current?.focus();
            }, 0);
          }}
          onFocus={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
          autoFocus
          style={{
            ...dropdownSearchStyle,
            direction: dropdownIsRTL ? "rtl" : "ltr",
            textAlign: dropdownIsRTL ? "right" : "left",
          }}
        />
      </div>
      {pagination &&
        (pagination.last_page > 1 ||
          (pagination.last_page === 1 && pagination.current_page === 1)) && (
          <div
            style={{
              padding: "8px",
              borderTop: "1px solid #333",
              background: "#14263b",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 6,
              }}
            >
              <span
                style={{
                  color: "#ccc",
                  fontSize: 11,
                  direction: dropdownIsRTL ? "rtl" : "ltr",
                  textAlign: dropdownIsRTL ? "right" : "left",
                }}
              >
                {t("salaries.add.pagination.page")} {pagination.current_page}{" "}
                {t("salaries.add.pagination.of")} {pagination.last_page}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 4,
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  if (pagination.current_page > 1) onPageChange(1);
                }}
                disabled={pagination.current_page === 1}
                style={{
                  padding: "4px 8px",
                  backgroundColor:
                    pagination.current_page === 1 ? "#2a2a2a" : "#1a1f2e",
                  color: pagination.current_page === 1 ? "#666" : "white",
                  border: "1px solid #444",
                  borderRadius: "4px",
                  cursor:
                    pagination.current_page === 1 ? "not-allowed" : "pointer",
                  fontSize: 10,
                  fontWeight: "bold",
                  minWidth: "28px",
                }}
              >
                ««
              </button>
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  if (pagination.current_page > 1)
                    onPageChange(pagination.current_page - 1);
                }}
                disabled={pagination.current_page === 1}
                style={{
                  padding: "4px 8px",
                  backgroundColor:
                    pagination.current_page === 1 ? "#2a2a2a" : "#1a1f2e",
                  color: pagination.current_page === 1 ? "#666" : "white",
                  border: "1px solid #444",
                  borderRadius: "4px",
                  cursor:
                    pagination.current_page === 1 ? "not-allowed" : "pointer",
                  fontSize: 10,
                  fontWeight: "bold",
                  minWidth: "28px",
                }}
              >
                ‹
              </button>
              {(() => {
                const pages = [];
                const current = pagination.current_page;
                const last = pagination.last_page;
                const showPages = 5;
                let start = Math.max(1, current - Math.floor(showPages / 2));
                let end = Math.min(last, start + showPages - 1);
                if (end - start < showPages - 1)
                  start = Math.max(1, end - showPages + 1);
                for (let i = start; i <= end; i++) pages.push(i);
                return pages.map(page => (
                  <button
                    key={page}
                    type="button"
                    onClick={e => {
                      e.stopPropagation();
                      onPageChange(page);
                    }}
                    style={{
                      padding: "4px 8px",
                      backgroundColor: page === current ? "#AC2000" : "#1a1f2e",
                      color: "white",
                      border:
                        page === current
                          ? "1px solid #AC2000"
                          : "1px solid #444",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: 10,
                      fontWeight: page === current ? "bold" : "normal",
                      minWidth: "28px",
                    }}
                  >
                    {page}
                  </button>
                ));
              })()}
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  if (pagination.current_page < pagination.last_page)
                    onPageChange(pagination.current_page + 1);
                }}
                disabled={pagination.current_page === pagination.last_page}
                style={{
                  padding: "4px 8px",
                  backgroundColor:
                    pagination.current_page === pagination.last_page
                      ? "#2a2a2a"
                      : "#1a1f2e",
                  color:
                    pagination.current_page === pagination.last_page
                      ? "#666"
                      : "white",
                  border: "1px solid #444",
                  borderRadius: "4px",
                  cursor:
                    pagination.current_page === pagination.last_page
                      ? "not-allowed"
                      : "pointer",
                  fontSize: 10,
                  fontWeight: "bold",
                  minWidth: "28px",
                }}
              >
                ›
              </button>
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  if (pagination.current_page < pagination.last_page)
                    onPageChange(pagination.last_page);
                }}
                disabled={pagination.current_page === pagination.last_page}
                style={{
                  padding: "4px 8px",
                  backgroundColor:
                    pagination.current_page === pagination.last_page
                      ? "#2a2a2a"
                      : "#1a1f2e",
                  color:
                    pagination.current_page === pagination.last_page
                      ? "#666"
                      : "white",
                  border: "1px solid #444",
                  borderRadius: "4px",
                  cursor:
                    pagination.current_page === pagination.last_page
                      ? "not-allowed"
                      : "pointer",
                  fontSize: 10,
                  fontWeight: "bold",
                  minWidth: "28px",
                }}
              >
                »»
              </button>
            </div>
          </div>
        )}
      <div style={{ maxHeight: 260, overflowY: "auto" }}>
        {(Array.isArray(items) ? items : []).length === 0 ? (
          <div
            style={{
              ...dropdownEmptyStyle,
              direction: dropdownIsRTL ? "rtl" : "ltr",
              textAlign: dropdownIsRTL ? "right" : "left",
            }}
          >
            {t("salaries.add.pagination.noResults")}
          </div>
        ) : (
          (Array.isArray(items) ? items : []).map(item => (
            <div
              key={item.id}
              style={{
                padding: "10px 12px",
                borderTop: "1px solid #333",
                cursor: "pointer",
                direction: dropdownIsRTL ? "rtl" : "ltr",
                textAlign: dropdownIsRTL ? "right" : "left",
              }}
              onClick={() => {
                onSelect(item);
                onClose?.();
              }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = "#2a2a2a";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              {(dropdownIsRTL ? item.name_ar : item.name_en) || item.name} -{" "}
              {item.employee_code}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AddSalary;
